import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { COMMS_UNREAD_KEY } from '@/api/comms/unread.api';
import {
  chatConversationsKey,
  chatThreadKey,
  deleteChatMessage,
  getConversationMessages,
  isTombstone,
  markConversationRead,
  sendChatMessage,
  type ChatMessage,
} from '@/api/comms/chat.api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import {
  conversationDisplayName,
  formatClockTime,
  formatDayLabel,
  isNewDay,
  senderName,
} from '@/lib/comms/format';
import { useToast } from '@/hooks/useToast';

/**
 * The open-conversation pane (Stream E, ADR-027 §7.2). The latest page
 * (newest 50) polls every 5s; "Load older" walks the keyset `before`
 * cursor and accumulates older pages in local state. Messages render
 * oldest-at-top / newest-at-bottom with day separators, own-vs-other
 * alignment, and body-less tombstones for deleted rows. Opening the
 * thread (and re-focusing the window) POSTs the read watermark; every
 * mutation invalidates the thread, the conversation list, and the unread
 * badge.
 */

interface ChatThreadProps {
  conversationId: string;
  myMembershipId: string | null;
  onBack?: () => void;
}

export function ChatThread({ conversationId, myMembershipId, onBack }: ChatThreadProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const latestQuery = useQuery({
    queryKey: chatThreadKey(conversationId),
    queryFn: () => getConversationMessages(conversationId),
    enabled: conversationId !== '',
    refetchInterval: 5000,
  });

  // Older pages accumulated by "Load older" (ascending, older than the
  // live latest page). Reset whenever the open conversation changes.
  const [olderItems, setOlderItems] = useState<ChatMessage[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  useEffect(() => {
    setOlderItems([]);
    setHasMoreOlder(false);
  }, [conversationId]);

  // Latest page is newest-first; flip to ascending for the bottom-anchored view.
  const latestAsc = useMemo(
    () => (latestQuery.data ? [...latestQuery.data.items].reverse() : []),
    [latestQuery.data],
  );

  // While no older pages are loaded, mirror the live "older exist" flag.
  useEffect(() => {
    if (olderItems.length === 0) setHasMoreOlder(latestQuery.data?.hasMore ?? false);
  }, [latestQuery.data?.hasMore, olderItems.length]);

  const messages = useMemo(() => {
    const byId = new Map<string, ChatMessage>();
    for (const m of olderItems) byId.set(m.id, m);
    for (const m of latestAsc) byId.set(m.id, m);
    return [...byId.values()].sort((a, b) => {
      if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
      return a.id < b.id ? -1 : 1;
    });
  }, [olderItems, latestAsc]);

  const conversation = latestQuery.data?.conversation ?? null;

  function invalidateAfterMutation(): void {
    void queryClient.invalidateQueries({ queryKey: chatThreadKey(conversationId) });
    void queryClient.invalidateQueries({ queryKey: chatConversationsKey() });
    void queryClient.invalidateQueries({ queryKey: COMMS_UNREAD_KEY });
  }

  // ── Read watermark: on open + on window focus ──────────────────────────
  const readMutation = useMutation({
    mutationFn: (id: string) => markConversationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatConversationsKey() });
      void queryClient.invalidateQueries({ queryKey: COMMS_UNREAD_KEY });
    },
  });
  // Keep the latest mutate in a ref so the focus listener isn't re-bound each render.
  const markRead = readMutation.mutate;
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  useEffect(() => {
    if (conversationId === '') return undefined;
    markReadRef.current(conversationId);
    const onFocus = (): void => {
      markReadRef.current(conversationId);
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [conversationId]);

  // ── Send ───────────────────────────────────────────────────────────────
  const [draft, setDraft] = useState('');
  const sendMutation = useMutation({
    mutationFn: (bodyText: string) => sendChatMessage(conversationId, bodyText),
    onSuccess: () => {
      setDraft('');
      invalidateAfterMutation();
    },
    onError: () => {
      toast.show('Could not send your message.', { variant: 'danger' });
    },
  });

  function submitDraft(): void {
    const body = draft.trim();
    if (body === '' || sendMutation.isPending) return;
    sendMutation.mutate(body);
  }

  function onComposerKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitDraft();
    }
  }

  // ── Delete own ───────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => deleteChatMessage(messageId),
    onSuccess: invalidateAfterMutation,
    onError: () => {
      toast.show('Could not remove the message.', { variant: 'danger' });
    },
  });

  // ── Load older ───────────────────────────────────────────────────────────
  function loadOlder(): void {
    const oldest = messages[0];
    if (!oldest || loadingOlder) return;
    setLoadingOlder(true);
    getConversationMessages(conversationId, { before: oldest.id })
      .then((page) => {
        const asc = [...page.items].reverse();
        setOlderItems((prev) => [...asc, ...prev]);
        setHasMoreOlder(page.hasMore);
      })
      .catch(() => {
        toast.show('Could not load older messages.', { variant: 'danger' });
      })
      .finally(() => {
        setLoadingOlder(false);
      });
  }

  // ── Auto-scroll to bottom when the newest message changes (not on prepend) ─
  const scrollRef = useRef<HTMLDivElement>(null);
  const newestId = messages.at(-1)?.id ?? null;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [newestId, conversationId]);

  const title = conversation ? conversationDisplayName(conversation, myMembershipId) : 'Conversation';

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-kw-border px-4 py-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to conversations"
            className="rounded-kw-md p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark md:hidden"
          >
            <ArrowLeft className="size-5" />
          </button>
        ) : null}
        <div className="min-w-0">
          <h2 className="truncate text-[14px] font-semibold text-kw-shark">{title}</h2>
          {conversation?.isGroup ? (
            <p className="text-[11px] text-kw-muted">{conversation.participants.length} people</p>
          ) : null}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-1 overflow-y-auto px-4 py-3">
        {hasMoreOlder ? (
          <div className="flex justify-center pb-2">
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingOlder}
              className="rounded-kw-full border border-kw-border bg-white px-3 py-1 text-[12px] text-kw-muted hover:bg-kw-bg disabled:opacity-50"
            >
              {loadingOlder ? 'Loading…' : 'Load older'}
            </button>
          </div>
        ) : null}

        {latestQuery.isLoading ? (
          <p className="py-6 text-center text-[13px] text-kw-muted">Loading messages…</p>
        ) : latestQuery.error ? (
          <p className="py-6 text-center text-[13px] text-kw-danger-text">
            Could not load this conversation.
          </p>
        ) : messages.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-kw-muted">No messages yet — say hello.</p>
        ) : (
          messages.map((message, i) => {
            const prev = i > 0 ? messages[i - 1] : null;
            const showDay = isNewDay(prev?.createdAt ?? null, message.createdAt);
            return (
              <div key={message.id}>
                {showDay ? (
                  <div className="my-3 flex items-center justify-center">
                    <span className="rounded-kw-full bg-kw-bg px-2.5 py-0.5 text-[11px] text-kw-muted">
                      {formatDayLabel(message.createdAt)}
                    </span>
                  </div>
                ) : null}
                <MessageBubble
                  message={message}
                  isOwn={message.senderMembershipId === myMembershipId}
                  authorName={senderName(conversation?.participants ?? [], message.senderMembershipId)}
                  onDelete={() => {
                    deleteMutation.mutate(message.id);
                  }}
                  deleting={deleteMutation.isPending}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          submitDraft();
        }}
        className="flex items-end gap-2 border-t border-kw-border px-4 py-3"
      >
        <textarea
          aria-label="Message"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={onComposerKeyDown}
          rows={1}
          placeholder="Write a message… (Enter to send, Shift+Enter for a new line)"
          className="max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
        <Button type="submit" size="sm" isLoading={sendMutation.isPending} disabled={draft.trim() === ''}>
          Send
        </Button>
      </form>
    </div>
  );
}

function MessageBubble({
  message,
  isOwn,
  authorName,
  onDelete,
  deleting,
}: {
  message: ChatMessage;
  isOwn: boolean;
  authorName: string;
  onDelete: () => void;
  deleting: boolean;
}) {
  const deleted = isTombstone(message);
  return (
    <div className={cn('group flex flex-col', isOwn ? 'items-end' : 'items-start')}>
      {!isOwn ? <span className="px-1 text-[11px] font-medium text-kw-muted">{authorName}</span> : null}
      <div className={cn('flex items-center gap-1.5', isOwn ? 'flex-row-reverse' : 'flex-row')}>
        <div
          className={cn(
            'max-w-[min(32rem,80%)] whitespace-pre-wrap break-words rounded-kw-lg px-3 py-1.5 text-[13px]',
            deleted
              ? 'bg-kw-bg italic text-kw-muted'
              : isOwn
                ? 'bg-kw-cerulean text-white'
                : 'bg-kw-bg text-kw-shark',
          )}
        >
          {deleted ? 'Message removed' : message.bodyText}
        </div>
        {isOwn && !deleted ? (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            aria-label="Delete message"
            className="rounded-kw-md p-1 text-kw-muted opacity-0 transition-opacity hover:text-kw-danger-text disabled:opacity-50 group-hover:opacity-100"
          >
            <Trash2 className="size-3.5" />
          </button>
        ) : null}
      </div>
      <span className="px-1 pt-0.5 text-[10px] text-kw-ghost">{formatClockTime(message.createdAt)}</span>
    </div>
  );
}
